import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormArray, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { v4 as uuidv4 } from "uuid";
import { FreightShipmentDto } from "../../../core/models/freight-quote/freightShipmentDto";
import { FreightQuoteService } from "../../../core/services/freight-quote.service";
import { JniToastrService } from "../../../core/services/jni-toastr.service";
import { CommonModule } from "@angular/common";
import { TableModule } from "primeng/table";
import { DialogModule } from "primeng/dialog";
import { ConfirmDialogComponent } from "../../../shared/components/confirm-dialog/confirm-dialog.component";
import { GetFreightEstimationTooltipManagement } from "../../../core/models/freight-quote/freightEstimationTooltipManagement";
import { FreightShipmentSectionDto } from "../../../core/models/freight-quote/freightShipmentSectionDto";
import { FreightShipmentSectionPricingDetailDto } from "../../../core/models/freight-quote/freightShipmentSectionPricingDetailDto";
import { InputTextModule } from "primeng/inputtext";
import { ButtonModule } from "primeng/button";

@UntilDestroy()
@Component({
  selector: "jni-freight-packages-form",
  templateUrl: "./freight-packages-form.component.html",
  styleUrls: ["./freight-packages-form.component.scss"],
  imports: [
    CommonModule,
    TableModule,
    DialogModule,
    ConfirmDialogComponent,
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
  ],
})
export class FreightPackagesFormComponent implements OnInit {
  constructor(
    private _toastr: JniToastrService,
    private _freightQuoteService: FreightQuoteService
  ) {}
  @Input() formArray!: FormArray;
  @Output() appendPackage = new EventEmitter<string>();
  @Output() removePackage = new EventEmitter<number>();
  @Output() removeShipment = new EventEmitter();
  @Input() currentShipment = 0;
  @Input() estimations!: FreightShipmentDto;
  @Input() pricingResponse!: FreightShipmentDto;
  @Input() shipmentReferenceId!: string;
  @Output() updatePackage = new EventEmitter<string>();
  @Output() updateWeight = new EventEmitter<string>();
  toolTips!: GetFreightEstimationTooltipManagement[];
  hasPriceList = false;
  displayPriceListModal = false;
  //packageList: any[] = [];
  selectedPackageModal!: FreightShipmentSectionPricingDetailDto[] | null;
  packageClearMessage = "You want clear packages";
  clearAlertVisible = false;
  showPricingDetailDialog = false;

  ngOnInit() {
    this.getFreightEstimationTooltip();
  }

  getFormGroup(index: number): FormGroup {
    return this.formArray.controls[index] as FormGroup;
  }

  addPackage() {
    this.appendPackage.emit();
  }

  removePackageAt(index: number) {
    if (index !== 0) {
      this.removePackage.emit(index);
      this.updatePackage.emit(this.shipmentReferenceId);
    }
  }

  onPackageChange() {
    this.updatePackage.emit(this.shipmentReferenceId);
  }

  packageWeightChange(packageDetail: {
    value: { packageReferenceId: string };
  }) {
    this.onPackageChange();
    this.updateWeight.emit(packageDetail.value.packageReferenceId);
  }

  totalWeight(packageDetail: {
    value: { packageCount: number; packageWeight: number };
  }) {
    return packageDetail.value.packageCount * packageDetail.value.packageWeight;
  }

  getFreightEstimationTooltip() {
    this._freightQuoteService
      .getFreightEstimationTooltipManagement()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          if (res?.data) {
            this.toolTips = res.data;
          } else {
            this._toastr.error("Error", "Failed to load");
          }
        },
        error: () => {
          this._toastr.error("Error", "Failed to load");
        },
      });
  }

  getTooltipByKey(searchKey: string): string | null {
    if (this.toolTips && this.toolTips.length > 0) {
      const foundItem = this.toolTips.find(item => item.key === searchKey)!;
      return foundItem ? foundItem.value : null;
    }
    return null;
  }

  clearPackages() {
    this.clearAlertVisible = true;
  }

  clearPackagesConfirm(event: boolean) {
    if (event) {
      if (this.formArray && this.formArray.length > 0) {
        this.formArray.controls.forEach(pkg => {
          if (pkg instanceof FormGroup) {
            Object.keys(pkg.controls).forEach(key => {
              const control = pkg.get(key);
              if (control !== null) {
                if (key === "weightUnitCode") {
                  control.patchValue("LB");
                } else if (key === "dimentsionUnitCode") {
                  control.patchValue("IN");
                } else if (key === "packageDescription") {
                  control.patchValue("");
                } else if (key === "packageReferenceId") {
                  control.patchValue(uuidv4());
                } else {
                  control.patchValue(0);
                }
              }
            });
          }
        });
      }
    }
    this.clearAlertVisible = false;
  }

  getPackagePricingList(
    response: FreightShipmentDto,
    shipmentReferenceId: string,
    index: number
  ) {
    const price = this.getRelatedPackagePrice(
      response,
      shipmentReferenceId,
      index
    );
    if (price) {
      return `<span class="text-decoration-underline text-primary">$${price.discountedTotalCharges}</span>`;
    }
    return `$0`;
  }

  // groupByEstimatedArrivalDate(packageItem: any) {
  //   const groupedPricing: Record<string, any[]> = {};
  //   packageItem.forEach((pricing: any) => {
  //     const estimatedDate = pricing.estimatedArrivalDate || "Unknown Date";

  //     if (!groupedPricing[estimatedDate]) {
  //       groupedPricing[estimatedDate] = [];
  //     }

  //     groupedPricing[estimatedDate].push(pricing);
  //   });

  //   const groupedArray = Object.keys(groupedPricing).map(date => ({
  //     estimatedDate: date,
  //     freightEstimationSectionPricings: groupedPricing[date],
  //   }));

  //   return groupedArray;
  // }

  showOrHidePricingModal(
    response: FreightShipmentDto,
    shipmentReferenceId: string,
    index: number
  ) {
    const price = this.getRelatedPackagePrice(
      response,
      shipmentReferenceId,
      index
    );
    this.selectedPackageModal = price
      ? price.freightEstimationSectionPricingDetails
      : null;

    if (!this.selectedPackageModal) {
      this.showPricingDetailDialog = true;
    } else {
      this.showPricingDetailDialog = true;
    }
  }

  getRelatedPackagePrice(
    response: FreightShipmentDto,
    shipmentReferenceId: string,
    index: number
  ) {
    if (!response?.freightEstimationSections) {
      return null;
    }

    const shipments = response.freightEstimationSections.filter(
      (s: FreightShipmentSectionDto) =>
        s.shipmentReferenceId === shipmentReferenceId
    );

    if (shipments.length === 0) {
      return null;
    }

    const pricing =
      this.pricingResponse?.freightEstimationSections
        ?.filter(s => s.shipmentReferenceId === shipmentReferenceId)
        .flatMap(s => s.freightEstimationSectionPricing) ?? [];
    const selectedPricing = pricing.filter(p => p.isSelected);
    if (selectedPricing.length === 1) {
      const shipment = shipments.find(
        s => s.id === selectedPricing[0]?.freightSectionId
      );
      const thisPackage = shipment?.freightEstimationSectionPackages?.[index];
      return (
        thisPackage?.freightEstimationPackagePricing?.find(
          p =>
            p.servicePricingUniqueId ===
            selectedPricing[0]?.servicePricingUniqueId
        ) ?? null
      );
    }

    if (shipments.length === 1 && pricing.length === 1) {
      const thisPackage =
        shipments[0]?.freightEstimationSectionPackages?.[index];
      if (thisPackage?.freightEstimationPackagePricing?.length === 1) {
        return thisPackage.freightEstimationPackagePricing[0];
      }
    }

    return null;
  }

  removeThisShipment() {
    this.removeShipment.emit();
  }
}
